import { AnimatePresence, motion } from "motion/react";
import { type ReactNode } from "react";

import ChatIcon from "~icons/heroicons/chat-bubble-left-right-20-solid";

import {
  type PatternsTextingEvent,
  type PatternsTextingMessage,
  type PatternsTextingResponseOptions,
} from "~/types";

import PatternsText from "./PatternsText";
import PatternsTextingNoticeAlert from "./PatternsTextingNoticeAlert";

import classes from "./PatternsTexting.module.css";

export interface PatternsTextingProps extends BoxProps {
  events: PatternsTextingEvent[];
  followOn: ReactNode;
  onResponseScoreChange?: (score: number) => void;
}

const MotionStack = motion.create(Stack);

const PatternsTexting: FC<PatternsTextingProps> = ({
  events: initialEvents,
  followOn,
  onResponseScoreChange,
  className,
  ...otherProps
}) => {
  const [events, setEvents] = useState(initialEvents);
  const [currentEventIndex, setCurrentEventIndex] = useState(0);
  const currentEvent = events[currentEventIndex];
  const completed = currentEventIndex === events.length;
  const [sentMessages, setSentMessages] = useState<PatternsTextingMessage[]>(
    [],
  );
  // const lastSentMessageReadingTimeMs = useMemo(() => {
  //   const lastMessage = sentMessages[sentMessages.length - 2];
  //   if (lastMessage?.body) {
  //     const { words } = readingTime(lastMessage.body);
  //     return (words / 250) * 60 * 1000;
  //   }
  // }, [sentMessages]);

  // == Next event
  const nextEvent = useCallback(() => {
    setCurrentEventIndex(i => i + 1);
    setSentMessages([]);
  }, []);

  // == Advance event
  const advanceEvent = useCallback(() => {
    if (!currentEvent) {
      return;
    }
    switch (currentEvent.type) {
      case "flurry": {
        const { messages } = currentEvent.flurry;
        const nextMessage = messages[sentMessages.length];
        if (nextMessage) {
          setSentMessages(prevMessages => [...prevMessages, nextMessage]);
        }
        break;
      }
      case "notice":
        break;
    }
  }, [currentEvent, sentMessages]);

  // == Auto-start event
  useEffect(() => {
    advanceEvent();
  }, [currentEvent]); // eslint-disable-line react-hooks/exhaustive-deps

  // == Scroll to bottom
  useEffect(scrollToBottom, [sentMessages.length]);

  // == Timeouts
  const scheduledActionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    return () => {
      const scheduleActionTimeout = scheduledActionTimeoutRef.current;
      if (scheduleActionTimeout) {
        clearTimeout(scheduleActionTimeout);
      }
    };
  }, []);

  // == Response options & scoring
  const [responseScore, setResponseScore] = useState(0);
  useEffect(() => {
    onResponseScoreChange?.(responseScore);
  }, [responseScore]); // eslint-disable-line react-hooks/exhaustive-deps
  const [shownResponseOptions, setShownResponseOptions] =
    useState<PatternsTextingResponseOptions | null>(null);
  useEffect(() => {
    if (
      currentEvent?.type === "flurry" &&
      currentEvent.flurry.messages.length === 0
    ) {
      setShownResponseOptions(currentEvent.flurry.response_options);
    }
  }, [currentEvent]);
  const selectOption = useCallback(
    (options: PatternsTextingResponseOptions, type: "aloof" | "empathic") => {
      const option = options[type];
      setShownResponseOptions(null);
      if (type === "empathic") {
        setResponseScore(prevScore => prevScore + 1);
      }
      setEvents(prevEvents => {
        const pastEvents = prevEvents.slice(0, currentEventIndex + 1);
        const upcomingEvents = prevEvents.slice(currentEventIndex + 1);
        return [...pastEvents, ...option.texting_events, ...upcomingEvents];
      });
    },
    [currentEventIndex],
  );

  return (
    <Stack
      className={cn("PatternsTexting-root", className)}
      gap={
        currentEvent?.type === "flurry" &&
        currentEvent.flurry.messages.length > 0
          ? "md"
          : 0
      }
      {...otherProps}
    >
      {(currentEventIndex > 0 ||
        (currentEvent &&
          currentEvent.type === "flurry" &&
          currentEvent.flurry.messages.length > 0)) && (
        <List
          listStyleType="none"
          style={{ display: "flex", flexDirection: "column" }}
        >
          {events.slice(0, currentEventIndex).flatMap((event, i) =>
            event.type === "notice" ? (
              <PatternsTextingNoticeAlert key={i} notice={event.notice} />
            ) : (
              <Flex
                key={i}
                direction="column"
                style={{
                  alignSelf:
                    event.flurry.from === "us" ? "flex-end" : "flex-start",
                }}
              >
                {event.flurry.messages.map((message, i) => (
                  <PatternsText
                    key={i}
                    {...{ message }}
                    from={event.flurry.from}
                  />
                ))}
              </Flex>
            ),
          )}
          {currentEvent &&
            (currentEvent.type === "notice" ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                onAnimationStart={scrollToBottom}
                onAnimationComplete={() => {
                  scheduledActionTimeoutRef.current = setTimeout(
                    nextEvent,
                    2000,
                  );
                }}
              >
                <PatternsTextingNoticeAlert notice={currentEvent.notice} />
              </motion.div>
            ) : (
              <Flex direction="column">
                {sentMessages.map((message, i) => (
                  <PatternsText
                    key={i}
                    {...{ message }}
                    from={currentEvent.flurry.from}
                    animate
                    onAnimationStart={scrollToBottom}
                    onAnimationComplete={() => {
                      if (
                        sentMessages.length <
                        currentEvent.flurry.messages.length
                      ) {
                        advanceEvent();
                      } else if (currentEvent.flurry.response_options) {
                        setShownResponseOptions(
                          currentEvent.flurry.response_options,
                        );
                      } else {
                        scheduledActionTimeoutRef.current = setTimeout(
                          nextEvent,
                          1000,
                        );
                      }
                    }}
                  />
                ))}
              </Flex>
            ))}
        </List>
      )}
      <AnimatePresence>
        {shownResponseOptions && (
          <MotionStack
            className={classes.optionsGroup}
            align="center"
            gap="xs"
            mod={{ reversed: currentEventIndex % 2 === 0 }}
            variants={{
              initial: { opacity: 0, y: 5 },
              entered: {
                opacity: 1,
                y: 0,
                transition: {
                  delay: 1,
                  type: "spring",
                  stiffness: 50,
                  damping: 15,
                  duration: 0.5,
                },
              },
              exited: { opacity: 0 },
            }}
            initial="initial"
            animate="entered"
            exit="exited"
            onAnimationStart={scrollToBottom}
            onAnimationComplete={animation => {
              if (animation === "exited") {
                nextEvent();
              }
            }}
          >
            <Button
              variant="filled"
              leftSection={<ChatIcon />}
              onClick={() => {
                selectOption(shownResponseOptions, "aloof");
              }}
            >
              {shownResponseOptions.aloof.label}
            </Button>
            <Button
              variant="filled"
              leftSection={<ChatIcon />}
              onClick={() => {
                selectOption(shownResponseOptions, "empathic");
              }}
            >
              {shownResponseOptions.empathic.label}
            </Button>
          </MotionStack>
        )}
        {completed && <>{followOn}</>}
      </AnimatePresence>
    </Stack>
  );
};

export default PatternsTexting;
